import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES, get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";

export const getHasOpenPlacementsNearbyApiUrl = (workerId: string) => {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/open-placements-nearby`;
};

function getHasOpenPlacementsNearbyQueryKey(
  workerId: string,
  queryParams?: {
    filter?: {
      useHiringWorkerTypes?: boolean;
    };
  }
) {
  const baseKey = [getHasOpenPlacementsNearbyApiUrl(workerId)];
  return queryParams ? [...baseKey, queryParams] : baseKey;
}

export const hasOpenPlacementsNearbyResponseSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.worker),
    attributes: z.object({
      hasOpenPlacementsNearby: z.boolean(),
    }),
  }),
});
export type HasOpenPlacementsNearbyResponse = z.infer<typeof hasOpenPlacementsNearbyResponseSchema>;

function transformResponse(response: HasOpenPlacementsNearbyResponse): boolean {
  const { data } = response;
  return data.attributes.hasOpenPlacementsNearby;
}

interface UseGetHasOpenPlacementsNearbyParams {
  workerId: string;
  useHiringWorkerTypes?: boolean;
}

export function useGetHasOpenPlacementsNearby(
  params: UseGetHasOpenPlacementsNearbyParams,
  options?: UseQueryOptions<boolean>
) {
  const { workerId, useHiringWorkerTypes } = params;

  const queryParams = isDefined(useHiringWorkerTypes)
    ? { filter: { useHiringWorkerTypes } }
    : undefined;

  return useQuery<boolean>({
    queryKey: getHasOpenPlacementsNearbyQueryKey(workerId, queryParams),
    queryFn: async () => {
      const response = await get({
        url: getHasOpenPlacementsNearbyApiUrl(workerId),
        queryParams,
        responseSchema: hasOpenPlacementsNearbyResponseSchema,
      });
      return transformResponse(response.data);
    },
    ...options,
  });
}
